import React, { useState, useEffect } from 'react';
import { getAllUsers, createUser, updateUser } from '../api';
import './UserManagement.css';

function UserManagement() {
    const [users, setUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [formData, setFormData] = useState({
        email: '',
        name: ''
    });

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setIsLoading(true);
        setError('');
        try {
            const usersData = await getAllUsers();
            setUsers(usersData);
        } catch (err) {
            setError('Failed to load data');
        } finally {
            setIsLoading(false);
        }
    };

    const handleCreate = () => {
        setEditingUser(null);
        setFormData({ email: '', name: '' });
        setShowModal(true);
    };

    const handleEdit = (user) => {
        setEditingUser(user);
        setFormData({
            email: user.email,
            name: user.name || ''
        });
        setShowModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        try {
            if (editingUser) {
                await updateUser(editingUser.email, {
                    name: formData.name
                });
            } else {
                await createUser(formData);
            }
            setShowModal(false);
            loadData();
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to save user');
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Never';
        return new Date(dateString).toLocaleString();
    };

    if (isLoading) {
        return (
            <div className="user-management">
                <div className="loading">Loading...</div>
            </div>
        );
    }

    return (
        <div className="user-management">
            <div className="page-header">
                <h2>User Management</h2>
                <button className="btn btn-primary" onClick={handleCreate}>
                    Add User
                </button>
            </div>

            {error && <div className="error-message">{error}</div>}

            <div className="users-table-container">
                <table className="users-table">
                    <thead>
                        <tr>
                            <th>Email</th>
                            <th>Name</th>
                            <th>Verified</th>
                            <th>Last Login</th>
                            <th>Created At</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.map((user) => (
                            <tr key={user.email}>
                                <td>{user.email}</td>
                                <td>{user.name || '-'}</td>
                                <td>
                                    <span className={`status-badge ${user.is_verified ? 'verified' : 'unverified'}`}>
                                        {user.is_verified ? 'Verified' : 'Unverified'}
                                    </span>
                                </td>
                                <td>{formatDate(user.last_login)}</td>
                                <td>{formatDate(user.created_at)}</td>
                                <td>
                                    <button
                                        className="btn btn-small btn-secondary"
                                        onClick={() => handleEdit(user)}
                                    >
                                        Edit
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {showModal && (
                <div className="modal-overlay" onClick={() => setShowModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h3>{editingUser ? 'Edit User' : 'Add User'}</h3>
                        <form onSubmit={handleSubmit}>
                            <div className="form-group">
                                <label>Email *</label>
                                <input
                                    type="email"
                                    value={formData.email}
                                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                    disabled={!!editingUser}
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label>Name</label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                />
                            </div>


                            <div className="modal-actions">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                                    Cancel
                                </button>
                                <button type="submit" className="btn btn-primary">
                                    {editingUser ? 'Update' : 'Create'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

export default UserManagement;
